import { useState } from "react";
import { useCreateObjection } from "@/hooks/useObjectionLibrary";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, X, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const categories = [
  { value: "price", label: "Price Objection" },
  { value: "timing", label: "Timing/Urgency" },
  { value: "competitor", label: "Competitor Comparison" },
  { value: "authority", label: "Decision Authority" },
  { value: "need", label: "Need/Fit" },
  { value: "trust", label: "Trust/Credibility" },
];

export function AddObjectionDialog() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState("");
  const [primaryResponse, setPrimaryResponse] = useState("");
  const [strategy, setStrategy] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const createObjection = useCreateObjection();

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      setKeywords([...keywords, keywordInput.trim().toLowerCase()]);
      setKeywordInput("");
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setKeywords(keywords.filter((k) => k !== keyword));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleGenerateResponse = async () => {
    if (!category || keywords.length === 0) {
      toast.error("Please select a category and add keywords first");
      return;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("ai-assistant", {
        body: {
          type: "objection_response",
          context: {
            category,
            keywords,
          },
        },
      });

      if (error) throw error;
      
      if (data?.response) {
        setPrimaryResponse(data.response);
        if (data.strategy) {
          setStrategy(data.strategy);
        }
      }
    } catch (error: any) {
      toast.error("Failed to generate response");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    await createObjection.mutateAsync({
      category,
      trigger_keywords: keywords,
      primary_response: primaryResponse,
      strategy: strategy || undefined,
    });

    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setCategory("");
    setKeywords([]);
    setKeywordInput("");
    setPrimaryResponse("");
    setStrategy("");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Objection Handler
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Objection Handler</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="category">Category *</Label>
            <Select value={category} onValueChange={setCategory} required>
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Trigger Keywords *</Label>
            <div className="flex gap-2">
              <Input
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add keyword and press Enter"
              />
              <Button type="button" variant="outline" onClick={handleAddKeyword}>
                Add
              </Button>
            </div>
            {keywords.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {keywords.map((keyword) => (
                  <Badge key={keyword} variant="secondary" className="gap-1">
                    {keyword}
                    <button
                      type="button"
                      onClick={() => handleRemoveKeyword(keyword)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="response">Suggested Response *</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleGenerateResponse}
                disabled={isGenerating || !category || keywords.length === 0}
              >
                <Sparkles className="h-4 w-4 mr-1" />
                {isGenerating ? "Generating..." : "AI Generate"}
              </Button>
            </div>
            <Textarea
              id="response"
              value={primaryResponse}
              onChange={(e) => setPrimaryResponse(e.target.value)}
              placeholder="Enter the suggested response for this objection..."
              rows={4}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="strategy">Strategy (Optional)</Label>
            <Input
              id="strategy"
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
              placeholder="e.g., Acknowledge, empathize, redirect to value"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={createObjection.isPending || !category || keywords.length === 0 || !primaryResponse}
            >
              {createObjection.isPending ? "Adding..." : "Add Handler"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
